import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Fingerprint, Camera, ShieldCheck, CheckCircle2, XCircle, Copy, ArrowLeft } from "lucide-react";
import {
  parseRegistrationOptions,
  credentialToJSON,
  passkeysSupported,
} from "@/lib/webauthn";

const COUNTRIES = [
  { code: "IN", name: "India" }, { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" }, { code: "AE", name: "UAE" },
  { code: "SG", name: "Singapore" }, { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" }, { code: "NZ", name: "New Zealand" },
];

const ID_TYPES = [
  { code: "aadhaar", name: "Aadhaar (India)" },
  { code: "ssn", name: "SSN (USA)" },
  { code: "national_id", name: "National ID" },
  { code: "driving_license", name: "Driving Licence" },
];

// Step titles used for the progress rail.
const STEPS = ["Photo", "Passport & PAN", "Citizen ID & Bank", "Passkey", "Done"];

export default function Wuid() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);      // { status, wuid, masked, reason }
  const [form, setForm] = useState({
    full_name: user?.name || "",
    dob: "",
    country: "IN",
    photo_base64: "",
    passport_number: "",
    pan: "",
    citizen_id: "",
    citizen_id_type: "aadhaar",
    bank_account: "",
    bank_ifsc: "",
  });

  useEffect(() => {
    if (!user) return;
    api.get("/wuid/me").then(({ data }) => {
      if (data) {
        setExisting(data);
        setStep(4);
        setResult({ status: "approved", wuid: data.wuid, masked: data.masked });
      }
    }).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="wuid-login">Login with Google</Button>
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitApply = async () => {
    if (!form.photo_base64) return toast.error("Please add a selfie photo");
    if (!form.full_name || !form.dob || !form.country) return toast.error("Fill your basic details");
    if (!form.passport_number || !form.pan) return toast.error("Passport and PAN required");
    if (!form.citizen_id || !form.bank_account) return toast.error("Citizen ID and bank account required");

    setBusy(true);
    try {
      const { data } = await api.post("/wuid/apply", form);
      setResult(data);
      if (data.status === "approved") {
        toast.success(`WUID minted: ${data.wuid}`);
        setStep(3);
      } else {
        toast.error(data.reason || "Application rejected");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  const enrollPasskey = async () => {
    if (!passkeysSupported()) return toast.error("Your browser does not support Passkeys");
    setBusy(true);
    try {
      const { data: options } = await api.post("/wuid/webauthn/register/begin");
      const publicKey = parseRegistrationOptions(options);
      const credential = await navigator.credentials.create({ publicKey });
      await api.post("/wuid/webauthn/register/complete", credentialToJSON(credential));
      toast.success("Passkey enrolled — WUID fully secured");
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Passkey enrollment failed");
    } finally { setBusy(false); }
  };

  const copyWuid = () => {
    navigator.clipboard.writeText(result?.wuid || "");
    toast.success("WUID copied");
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="wuid-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/account")} data-testid="wuid-back">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <h1 className="heading text-2xl font-extrabold">World Unique ID (WUID)</h1>
      </div>

      <StepRail step={step} total={STEPS.length} labels={STEPS} />

      {existing && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="wuid-existing">
          <CardContent className="p-4 flex items-start gap-3">
            <ShieldCheck className="text-emerald-600 mt-0.5" size={20} />
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">Your WUID: <span className="font-mono">{existing.wuid}</span></div>
              <div className="text-emerald-800 mt-0.5">Approved on {new Date(existing.approved_at).toLocaleString()} · {existing.passkeys?.length || 0} passkey(s) linked</div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 0 && !existing && (
        <Card><CardHeader><CardTitle>1 · Your photo</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <PhotoCapture value={form.photo_base64} onChange={(v) => set("photo_base64", v)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full legal name">
                <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="wuid-full-name" />
              </Field>
              <Field label="Date of birth (YYYY-MM-DD)">
                <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} data-testid="wuid-dob" />
              </Field>
              <Field label="Country">
                <Select value={form.country} onValueChange={(v) => set("country", v)}>
                  <SelectTrigger data-testid="wuid-country"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="pt-2">
              <Button disabled={!form.photo_base64 || !form.full_name || !form.dob} onClick={() => setStep(1)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="wuid-next-1">
                Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && !existing && (
        <Card><CardHeader><CardTitle>2 · Passport & Tax ID</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Field label="Passport number">
              <Input value={form.passport_number} onChange={(e) => set("passport_number", e.target.value.toUpperCase())} data-testid="wuid-passport" placeholder="e.g. K1234567" />
            </Field>
            <Field label="PAN / Tax ID">
              <Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} data-testid="wuid-pan" placeholder="e.g. ABCDE1234F" />
            </Field>
            <div className="pt-2 flex gap-2">
              <Button variant="outline" onClick={() => setStep(0)} className="rounded-full" data-testid="wuid-back-1">Back</Button>
              <Button disabled={!form.passport_number || !form.pan} onClick={() => setStep(2)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="wuid-next-2">
                Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && !existing && (
        <Card><CardHeader><CardTitle>3 · Citizen ID & Bank account</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Citizen ID type">
                <Select value={form.citizen_id_type} onValueChange={(v) => set("citizen_id_type", v)}>
                  <SelectTrigger data-testid="wuid-cid-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ID_TYPES.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Citizen ID number">
                <Input value={form.citizen_id} onChange={(e) => set("citizen_id", e.target.value)} data-testid="wuid-cid" />
              </Field>
            </div>
            <Field label="Bank account number">
              <Input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} data-testid="wuid-bank" />
            </Field>
            <Field label="IFSC / SWIFT (optional)">
              <Input value={form.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} data-testid="wuid-ifsc" />
            </Field>
            <div className="pt-2 flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-full" data-testid="wuid-back-2">Back</Button>
              <Button disabled={busy || !form.citizen_id || !form.bank_account} onClick={submitApply} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="wuid-submit">
                {busy ? "Verifying…" : "Submit for WUID"}
              </Button>
            </div>
            {result?.status === "rejected" && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900" data-testid="wuid-reject">
                <XCircle size={16} className="mt-0.5" />
                <div><b>Rejected.</b> {result.reason}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 3 && result?.status === "approved" && (
        <Card><CardHeader><CardTitle className="flex items-center gap-2"><Fingerprint size={20} /> 4 · Link a Passkey</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-stone-700">Your device biometric (Face ID / Windows Hello / Android fingerprint) will become your second factor for high-value actions — wallet withdrawals, dealer transfers, gold/silver conversions.</p>
            <div className="p-3 rounded-lg bg-stone-50 text-sm font-mono text-center">WUID: <b>{result.wuid}</b></div>
            <div className="pt-2">
              <Button disabled={busy} onClick={enrollPasskey} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="wuid-enroll-passkey">
                {busy ? "Waiting for device…" : "Link this device"}
              </Button>
              <Button variant="ghost" onClick={() => setStep(4)} className="rounded-full ml-2" data-testid="wuid-skip-passkey">
                Skip for now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card className="border-emerald-300"><CardHeader><CardTitle className="flex items-center gap-2 text-emerald-900"><CheckCircle2 size={22} /> WUID active</CardTitle></CardHeader>
          <CardContent className="space-y-3" data-testid="wuid-success">
            <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 text-center">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">World Unique ID</div>
              <div className="text-4xl font-mono font-black tracking-widest mt-2 text-emerald-900" data-testid="wuid-value">{result?.wuid || existing?.wuid}</div>
              <Button variant="outline" size="sm" onClick={copyWuid} className="rounded-full gap-2 mt-3" data-testid="wuid-copy"><Copy size={14} /> Copy</Button>
            </div>
            <div className="text-xs text-stone-500">Use this ID for high-value actions inside Find2Near — dealer investments, gold/silver conversions and P2P cash exchange.</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ---------------------------------- little pieces ----------------------------------
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function StepRail({ step, total, labels }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {labels.map((l, i) => (
        <React.Fragment key={l}>
          <div className={`flex items-center gap-1.5 ${i <= step ? "text-[#E05E36]" : "text-stone-400"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${i <= step ? "bg-[#E05E36] text-white" : "bg-stone-200"}`}>{i + 1}</div>
            <span className="hidden sm:inline font-semibold">{l}</span>
          </div>
          {i < total - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-[#E05E36]" : "bg-stone-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function PhotoCapture({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be under 5 MB");
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => { onChange(reader.result); setUploading(false); };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 rounded-xl bg-stone-100 border-2 border-dashed border-stone-300 overflow-hidden flex items-center justify-center">
        {value ? <img loading="lazy" src={value} alt="Your selfie" className="w-full h-full object-cover" /> : <Camera className="text-stone-400" size={32} />}
      </div>
      <div>
        <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-full gap-2" data-testid="wuid-photo-btn">
          <Camera size={14} /> {value ? "Replace photo" : uploading ? "Reading…" : "Add photo"}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="wuid-photo-input" />
        <div className="text-xs text-stone-500 mt-1">Face clearly visible · under 5 MB · JPG/PNG</div>
      </div>
    </div>
  );
}
